import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { UseOutletContextPosSale } from './PosSale';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { PosSaleDA } from '../../../../services/admin/pos.sale.service';
import { PosSale } from '../../../../models/pos.sale.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatContact, formatCurrency, formatMoney, formatNumberAddress, formatTaxRate, normalizeContactData } from '../../../../scripts/format.value.script';
import { datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { contactTypeValue } from '../../../../libraries/contact.type.library';
import { countryName } from '../../../../libraries/country.library';
import { languageValue } from '../../../../libraries/language.library';
import { inventoryTypeValue } from '../../../../libraries/inventory.type.library';
import { discountDescription } from '../../../../libraries/discount.library';
import { paymentMethodValue } from '../../../../libraries/payment.library';
import { treasuryAccountSelected } from '../../../../scripts/list.selected.script';

import AppPreloader from '../../../../components/Preloader';
import AppPurchaseViewDraft from '../../purchase/component/PurchaseViewDraft';

export interface SaleViewPageProps {}

let errorResponse: Error, posSaleResponse: PosSale;

const SaleViewPage: React.FunctionComponent<SaleViewPageProps> = () => {
  const { setRoute, companyForUser, settingsForCompany ,treasuryAccountsForCompany } = UseOutletContextPosSale();
  const { lang } = StateLanguage();
  const param = useParams()

  const [mounted, setMounted] = useState(false);
  const [posSale, setPosSale] = useState<PosSale | null>(null);

  const loadPosSale = async (id_company: string, id_pos_sale: string) => {
    await PosSaleDA.getSale(id_company, id_pos_sale).then((response) => {
      if (response.status === 200) {
        posSaleResponse = response.data
        normalizeContactData(posSaleResponse.customer)
        setPosSale(posSaleResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error);
    })
  }

  function hasMultiCurrencyPayments(): boolean {
    if (!posSale) return false
    return posSale.payments.some(p => p.currency_code && p.currency_code !== 'CHF')
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadPosSale(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect(() => {
    setMounted(true)
    setRoute({ path: {root: lang.labels.pos, branch: lang.labels.viewSale}, company: false })

    return () => setMounted(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mounted) return null;

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && treasuryAccountsForCompany && posSale
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
          <div className="card card-flush mb-5">
            <div className="card-header pt-3">
              <div className="card-title align-items-start flex-column">
                <h2>{posSale.sale_number}</h2>
              </div>
              <div className="card-toolbar">
              </div>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap">
                <div className="flex-equal me-5">
                  <table className="table align-middle gy-1 m-0">
                    <tbody className="fw-bold">
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.company}:</td>
                        <td className="min-w-200px">{posSale.company.name}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.pos}:</td>
                        <td className="min-w-200px">{posSale.pos.name}</td>
                      </tr>
                      <tr>
                        <td valign="top" className="w-200px text-muted">{lang.labels.customer}:</td>
                        <td className="min-w-200px">
                          <div className="d-flex align-items-center">
                            {formatContact(posSale.customer)}
                            <span className="badge badge-sm badge-light text-uppercase fs-9 ms-3">{contactTypeValue(lang, posSale.customer.type)}</span>
                          </div>
                          { posSale.customer.data?.address &&
                            <>
                              <p className="text-secondary fs-8 p-0 m-0">{posSale.customer.data.address.street} {formatNumberAddress(lang, posSale.customer.data.address.number)}</p>
                              <p className="text-secondary fs-8 p-0 m-0">{posSale.customer.data.address.city}{(posSale.customer.data.address.zip_code ?? '').length > 0 && ` - ${posSale.customer.data.address.zip_code}`}</p>
                              <p className="text-secondary fs-8 p-0 m-0">{countryName(lang.code, posSale.customer.data.address.country)}</p>
                            </>
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex-equal">
                  <table className="table align-middle gy-1 m-0">
                    <tbody className="fw-bold">
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.createdAt}:</td>
                        <td className="min-w-200px">{datetimeFormat12h(lang.code, posSale.created_at)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.currency}:</td>
                        <td className="min-w-200px">{formatCurrency(posSale.currency)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.language}:</td>
                        <td className="min-w-200px">{languageValue(lang, posSale.language)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h4>{lang.labels.requirements}</h4>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed gy-2 mb-2">
                    <thead className="border-bottom">
                      <tr className="text-muted text-uppercase fw-bolder fs-7">
                        <th className="min-w-200px text-nowrap text-start">{lang.labels.description}</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.quantity}</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.unitPrice} ({posSale.currency.code})</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.rebate} ({posSale.currency.code})</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.taxRate} ({posSale.currency.code})</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.priceGross} ({posSale.currency.code})</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.priceNet} ({posSale.currency.code})</th>
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      { posSale.requirements.map (( (item, index) => { return (
                        <tr key={index}>
                          <td className="text-start">
                            <div className="d-flex flex-column">
                              {item.description}
                              <span className="fs-9 fw-normal text-muted">{inventoryTypeValue(lang, item.type)}</span>
                            </div>
                          </td>
                          <td className="text-nowrap text-end">{item.quantity} {item.unit.symbol}</td>
                          <td className="text-nowrap text-end">{formatMoney(item.price_unit)}</td>
                          <td className="text-nowrap text-end">
                            <div className="d-flex align-items-center justify-content-end">
                              {formatMoney(item.rebate_amount)}
                              <span className="badge badge-sm badge-light w-85px fs-9 ms-2">{discountDescription(lang, posSale.currency, item.rebate)}</span>
                            </div>
                          </td>
                          <td className="text-nowrap text-end">
                            <div className="d-flex align-items-center justify-content-end">
                              {formatMoney(item.tax_value ?? item.tax_amount ?? 0)}
                              <span className="badge badge-sm badge-light w-75px fs-9 ms-2">{formatTaxRate(item.tax_rate)} %</span>
                            </div>
                          </td>
                          <td className="text-nowrap text-end">{formatMoney(item.price_gross)}</td>
                          <td className="text-nowrap text-end">{formatMoney(item.price_net)}</td>
                        </tr>
                      )}))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h4>{lang.labels.payments}</h4>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed gy-2 mb-2">
                    <thead className="border-bottom">
                      <tr className="text-muted text-uppercase fw-bolder fs-7">
                        <th className="w-25px text-nowrap text-start">{lang.labels.n}</th>
                        <th className="min-w-100px text-nowrap text-start">{lang.labels.method}</th>
                        <th className="min-w-100px text-nowrap text-start">{lang.labels.sourceOfIncome}</th>
                        { hasMultiCurrencyPayments() &&
                          <th className="min-w-80px text-nowrap text-start">{lang.labels.currency}</th>
                        }
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.received} ({posSale.currency.code})</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.change} ({posSale.currency.code})</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.amount} ({posSale.currency.code})</th>
                        { hasMultiCurrencyPayments() &&
                          <th className="min-w-100px text-nowrap text-end">{lang.labels.amountCHF ?? 'Amount (CHF)'}</th>
                        }
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      { posSale.payments.map (( (item, index) => { return (
                        <tr key={index} className="row-dashed">
                          <td>{index + 1}</td>
                          <td>{paymentMethodValue(lang, item.method)}</td>
                          <td>{(item.source === "credit-note" || item.id_accounting_entry) ? lang.labels.creditNote : treasuryAccountSelected(treasuryAccountsForCompany, item.source ?? item.id_treasury_account)}</td>
                          { hasMultiCurrencyPayments() &&
                            <td>
                              {item.currency_code || 'CHF'}
                              { item.currency_code && item.currency_code !== 'CHF' && item.exchange_rate &&
                                <span className="text-muted fs-9 ms-1">({item.exchange_rate})</span>
                              }
                            </td>
                          }
                          <td className="text-nowrap text-end">{formatMoney(item.received)}</td>
                          <td className="text-nowrap text-end">{formatMoney(item.change ?? item.change_amount)}</td>
                          <td className="text-nowrap text-end">{formatMoney(item.amount)}</td>
                          { hasMultiCurrencyPayments() &&
                            <td className="text-nowrap text-end">
                              { item.currency_code && item.currency_code !== 'CHF'
                                ? formatMoney(item.amount_chf)
                                : formatMoney(item.amount)
                              }
                            </td>
                          }
                        </tr>
                      )}))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <h4>{lang.labels.amount}</h4>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="d-flex justify-content-end">
                <div className="mw-500px">
                  <div className="d-flex flex-stack px-5 py-2 bg-light-info border-top border-bottom">
                    <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.subTotal}:</div>
                    <div className="text-end fw-bolder ps-20">
                      {posSale.currency.code} {formatMoney(posSale.amount.sub_total)}
                    </div>
                  </div>
                  <div className="d-flex flex-stack px-5 py-2 bg-light-info border-bottom">
                    <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.rebateTotal}:</div>
                    <div className="text-end fw-bolder ps-20">{posSale.currency.code} {formatMoney(posSale.amount.rebate_total)}</div>
                  </div>
                  <div className="d-flex flex-stack px-5 py-2 bg-light-info border-bottom">
                    <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.taxTotal}:</div>
                    <div className="text-end fw-bolder ps-20">
                      {posSale.currency.code} {formatMoney(posSale.amount.tax_total)}
                    </div>
                  </div>
                  { posSale.amount.tax_list.map (( (item, index) => { return (
                    <div key={index} className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
                      <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.tax}: {formatTaxRate(item.tax_rate)} %</div>
                      <div className="text-end fw-bolder ps-20 fs-7">{posSale.currency.code} {formatMoney(item.tax_value)}</div>
                    </div>
                  )}))}
                  <div className="d-flex flex-stack px-5 py-2 bg-light-warning border-top border-bottom">
                    <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.total}:</div>
                    <div className="text-end fw-bolder ps-20">
                      {posSale.currency.code} {formatMoney(posSale.amount.total)}
                    </div>
                  </div>
                  <div className="d-flex flex-stack px-5 py-2 bg-light-success border-top border-bottom">
                    <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.payed}:</div>
                    <div className="text-end fw-bolder ps-20">{posSale.currency.code} {formatMoney(posSale.amount.paid)}</div>
                  </div>
                  { posSale.amount.paid_list.cash > 0 &&
                    <div className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
                      <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.cash}</div>
                      <div className="text-end fw-bolder ps-20 fs-7">{posSale.currency.code} {formatMoney(posSale.amount.paid_list.cash)}</div>
                    </div>
                  }
                  { posSale.amount.paid_list.card > 0 &&
                    <div className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
                      <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.card}</div>
                      <div className="text-end fw-bolder ps-20 fs-7">{posSale.currency.code} {formatMoney(posSale.amount.paid_list.card)}</div>
                    </div>
                  }
                  { posSale.amount.paid_list.deposit > 0 &&
                    <div className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
                      <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.deposit}</div>
                      <div className="text-end fw-bolder ps-20 fs-7">{posSale.currency.code} {formatMoney(posSale.amount.paid_list.deposit)}</div>
                    </div>
                  }
                  <div className="d-flex flex-stack px-5 py-2 bg-light-danger border-top border-bottom">
                    <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.change}:</div>
                    <div className="text-end fw-bolder ps-20">{posSale.currency.code} {formatMoney(posSale.amount.change)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          <AppPurchaseViewDraft record={posSale} showGeneratedPdf={true} companyId={companyForUser?.id} pdfPath="pos/sales" documentId={posSale?.id}></AppPurchaseViewDraft>
        </div>
        :
        <AppPreloader />
      }
    </div>
  );
};

export default SaleViewPage;
